import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { VehiclesService } from 'src/app/shared/api-services/vehicles.service';
import { CommunicationService } from 'src/app/shared/utilities/comunication.service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Subject, of, switchMap, takeUntil } from 'rxjs';
import { VehicleDisableRequest } from 'src/app/shared/models/vehicle-disable/vehicle-disable-request';
import { AutocompleteModule } from '../../../inputs/autocomplete-primeng/autocomplete.module';
import { UserGroup } from 'src/app/shared/models/user-group/user-group';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Vehicle } from 'src/app/shared/models/vehicle/vehicle';
import { VehiclesFilter } from 'src/app/shared/models/vehicle/vehicle-filter';
import { VehicleDisable } from 'src/app/shared/models/vehicle-disable/vehicle-disable';
import { VehicleDisablesFilter } from 'src/app/shared/models/vehicle-disable/vehicle-disable-filter';
import { VehicleDisableResponse } from 'src/app/shared/models/vehicle-disable/vehicle-disable-response';

// Interfaccia per tenere traccia del risultato di ogni veicolo
interface VehicleResult {
  id: string;
  success: boolean;
  displayName?: string;
  errorMessage?: string;
}

// Interfaccia per il risultato dell'operazione
interface OperationResult {
  success: boolean;
  canceled?: boolean;
  errorMessage?: string;
}

@Component({
    selector: 'vehicle-active-massive-modal',
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        AutocompleteModule
    ],
    templateUrl: './vehicle-active-massive-modal.component.html',
    styleUrls: ['./vehicle-active-massive-modal.component.scss']
})

export class VehicleActiveMassiveModalComponent implements OnInit, OnDestroy {

  @Input() vehicleIds: string[];
  vehicles: Vehicle[];

  form: FormGroup;
  selectedUserGroups: UserGroup[] = [];
  
  // Variabili per la barra di progresso
  onApplyProgressStep = false;
  progressBarValue = 0;
  elementProcessed = 0;
  totalElements = 0;

  // Variabili per il riepilogo dei risultati
  showSummary = false;
  processResults: VehicleResult[] = [];
  successCount = 0;
  failureCount = 0;

  private _destroy$ = new Subject<void>();

  constructor(
    public bsModalRef: BsModalRef,
    private _vehiclesService: VehiclesService,
    private _communicationService: CommunicationService,
    private _toastrService: ToastrService,
    private _translateService: TranslateService
  ) {
    this.form = new FormGroup({
      disableFrom: new FormControl(null, [Validators.required]),
      disableFromTime: new FormControl(null, [Validators.required]),
      disableTo: new FormControl(null),
      disableToTime: new FormControl(null),
      note: new FormControl(null),
      userGroupIds: new FormControl([])
    });
  }

  ngOnInit(): void {
    // Precarica i dettagli dei veicoli
    this.getVehicles();
  }

  private getVehicles() {
    if (this.vehicleIds && this.vehicleIds.length > 0) {
      const vehicleFilter: VehiclesFilter = {
        idsList: this.vehicleIds.join(',')
      };
      this._vehiclesService.listVehiclesNew$(vehicleFilter)
        .pipe(takeUntil(this._destroy$))
        .subscribe(vehicles => {
          this.vehicles = vehicles.items;
        });
    }
  }

  onSubmit() {    
    // Reset dei risultati
    this.processResults = [];
    this.successCount = 0;
    this.failureCount = 0;
    
    // Attiva la barra di progresso
    this.onApplyProgressStep = true;
    this.totalElements = this.vehicleIds.length;
    this.elementProcessed = 0;
    this.progressBarValue = 0;

    // Inizia a processare i veicoli
    this.processNextVehicle(0);
  }

  private processNextVehicle(index: number): void {
    if (index >= this.vehicleIds.length) {
      // Abbiamo finito di processare tutti i veicoli
      this._toastrService.success(this._translateService.instant('VEHICLE_DISABLE.SUCCESS_OPERATION'));
      this.showSummary = true;
      this._communicationService.sendEvent();
      return;
    }

    const vehicleId = this.vehicleIds[index];
    const currentVehicle = this.vehicles.find(v => v.id === vehicleId);
    const now = new Date().toISOString();

    const filter : VehicleDisablesFilter = {
      inRangeDate: now
    }

    // Trova il veicolo disabilitato corrente
    this._vehiclesService.listVehicleDisables$(vehicleId, filter)
      .pipe(
        takeUntil(this._destroy$),
        switchMap((response: VehicleDisableResponse) => {
          
          if (response && response.count > 0) {
            const activeDisable = response.items[0];
            const request = this.mapRequest(activeDisable);

            return this._vehiclesService.updateVehicleDisable$(vehicleId, activeDisable.id, request).pipe(
              switchMap(() => of({ success: true } as OperationResult)),
              takeUntil(this._destroy$)
            );
          }
          // Se non ci sono sospensioni attive, segnala che il veicolo è già attivo
          return of({ 
            success: false, 
            errorMessage: this._translateService.instant('VEHICLE_DISABLE.VEHICLE_ALREADY_ACTIVE') 
          } as OperationResult);
        })
      )
      .subscribe({
        next: (result: OperationResult) => {
          // Aggiorna la barra di progresso
          this.elementProcessed++;
          this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
          
          if (result.success) {
            // Registra il successo
            this.processResults.push({
              id: vehicleId,
              success: true,
              displayName: currentVehicle?.displayName || vehicleId
            });
            this.successCount++;
          } else {
            // Registra il fallimento
            this.processResults.push({
              id: vehicleId,
              success: false,
              displayName: currentVehicle?.displayName || vehicleId,
              errorMessage: result.errorMessage || this._translateService.instant('ERRORS.GENERIC')
            });
            this.failureCount++;
          }
          
          // Procedi con il prossimo veicolo
          this.processNextVehicle(index + 1);
        },
        error: (error: any) => {
          // Aggiorna la barra di progresso in caso di errore
          this.elementProcessed++;
          this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
          
          // Registra il fallimento
          this.processResults.push({
            id: vehicleId,
            success: false,
            displayName: currentVehicle?.displayName || vehicleId,
            errorMessage: error?.error?.message || this._translateService.instant('ERRORS.GENERIC')
          });
          this.failureCount++;
          
          // Procedi con il prossimo veicolo
          this.processNextVehicle(index + 1);
        }
      });
  }

  private mapRequest(vehicleDisable: VehicleDisable): VehicleDisableRequest {
    const now = new Date();
    return {
      disabledFrom: vehicleDisable.disabledFrom,
      disabledTo: now.toISOString(),
      note: this.form.value.note || vehicleDisable.note,
      userGroupIds: vehicleDisable.userGroupIds
    };
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
